import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaStar, FaUserLock, FaStoreAlt, FaShieldAlt, FaChevronDown,
    FaExclamationCircle, FaSignOutAlt, FaColumns
} from "react-icons/fa";

import PublicBackMethods from "./PublicBackMethods";
import RatingModal from "./RatingModal";
import ComplaintModal from "./ComplaintModal";
import LoginModal from "./LoginModal";
import PublicComplaintsList from "./PublicComplaintsList";
// استيراد المكون المنفصل
import PhoneVerificationModal from "./Complaint/PhoneVerificationModal";

export default function PublicDashboard() {
    const { public_request, savePublicLogin, accountToken, establishmentUid, publicLogout } = PublicBackMethods();

    const navigate = useNavigate();
    const { id } = useParams();
    const [establishment, setEstablishment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    // States للتحقق من الهاتف
    const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
    const [verifiedPhone, setVerifiedPhone] = useState("");
    const [phoneToken, setPhoneToken] = useState("");

    const sectionRef = useRef(null);
    const isOwner = useMemo(() => accountToken && establishmentUid === id, [accountToken, establishmentUid, id]);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const response = await public_request.get(`/public/establishments/${id}`);
                setEstablishment(response.data);
            } catch (error) { console.error("Error:", error); }
            finally { setLoading(false); }
        };
        if (id) fetchDetails();
    }, [id, public_request]);

    const toggleSection = (section) => {
        if (activeSection === section) {
            setActiveSection(null);
        } else {
            // إذا أراد المستخدم تقديم شكوى ولم يتم التحقق من هاتفه بعد
            if (section === 'complaint' && !phoneToken) {
                setIsPhoneModalOpen(true);
                return;
            }
            setActiveSection(section);
            setTimeout(() => { sectionRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
        }
    };

    const menuItems = useMemo(() => {
        if (isOwner) {
            return [
                { id: 1, title: "بيانات الفعالية", subtitle: "الضبوط والزيارات", icon: <FaColumns />, gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", action: () => navigate(`/establishment-dashboard/${id}`) },
                { id: 5, title: "خروج", subtitle: "إنهاء الجلسة", icon: <FaSignOutAlt />, gradient: "linear-gradient(135deg, #232526 0%, #414345 100%)", action: () => publicLogout() }
            ];
        } else {
            return [
                { id: 2, title: "تقديم شكوى", subtitle: "حماية المستهلك", icon: <FaExclamationCircle />, gradient: "linear-gradient(135deg, #f85032 0%, #e73827 100%)", action: () => toggleSection('complaint') },
                { id: 3, title: "تقييم الفعالية", subtitle: "شاركنا تجربتك", icon: <FaStar />, gradient: "linear-gradient(135deg, #f1b400 0%, #f18a00 100%)", action: () => toggleSection('rating') },
                { id: 4, title: "تسجيل الدخول", subtitle: "لأصحاب المنشآت", icon: <FaUserLock />, gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)", action: () => setIsLoginModalOpen(true) }
            ];
        }
    }, [isOwner, id, phoneToken, activeSection, navigate, publicLogout]);

    if (loading) return <div className="loader-wrapper" style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0f172a' }}><div className="spinner-border text-light"></div></div>;

    return (
        <div className="main-wrapper" dir="rtl">
            <div className="content-container">
                <header className="hero-section text-center pt-5">
                    <div className="gov-badge mb-4">
                        <FaShieldAlt className="ms-2" />
                        {isOwner ? "مرحباً بك، صاحب المنشأة" : "الإدارة العامة للتجارة الداخلية وحماية المستهلك"}
                    </div>
                    <div className="main-card-info shadow">
                        <div className="store-icon-wrapper"><FaStoreAlt /></div>
                        <h1 className="est-name-highlight">{establishment?.name || "تحميل..."}</h1>
                    </div>
                </header>

                <div className="container py-5">
                    <div className="row g-4 justify-content-center">
                        {menuItems.map((item) => (
                            <div key={item.id} className="col-lg-3 col-md-6">
                                <div className={`glass-card ${((activeSection === 'complaint' && item.id === 2) || (activeSection === 'rating' && item.id === 3)) ? 'active' : ''}`} onClick={item.action}>
                                    <div className="card-icon" style={{ background: item.gradient }}>{item.icon}</div>
                                    <div className="card-text"><h3>{item.title}</h3><p>{item.subtitle}</p></div>
                                    <FaChevronDown className={`chevron ${activeSection && item.id === (activeSection === 'complaint' ? 2 : 3) ? 'rotate' : ''}`} />
                                </div>
                            </div>
                        ))}
                    </div>

                    <PublicComplaintsList establishmentId={id} public_request={public_request} />

                    <div ref={sectionRef} className="dynamic-content-area mt-5">
                        {activeSection === 'complaint' && (
                            <ComplaintModal
                                isOpen={true}
                                onClose={() => setActiveSection(null)}
                                establishmentId={id}
                                establishmentName={establishment.name}
                                backEnd_request={public_request}
                                verifiedPhone={verifiedPhone}
                                phoneToken={phoneToken}
                                onTokenInvalid={() => { setPhoneToken(""); setIsPhoneModalOpen(true); }}
                            />
                        )}
                        {activeSection === 'rating' && (
                            <RatingModal isOpen={true} onClose={() => setActiveSection(null)} establishmentId={id} backEnd_request={public_request} isInline={true} />
                        )}
                    </div>
                </div>
            </div>

            {/* مودالات مستقلة */}
            <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} fieldUid={id} backEnd_request={public_request} savePublicLogin={savePublicLogin} />

            <PhoneVerificationModal
                isOpen={isPhoneModalOpen}
                onClose={() => setIsPhoneModalOpen(false)}
                public_request={public_request}
                onVerified={(phone, token) => {
                    setVerifiedPhone(phone);
                    setPhoneToken(token);
                    setIsPhoneModalOpen(false);
                    setActiveSection('complaint');
                }}
            />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;900&display=swap');
                .main-wrapper { background: #0f172a; min-height: 100vh; font-family: 'Cairo', sans-serif; color: white; position: relative; }
                .main-card-info { background: rgba(255, 255, 255, 0.05); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 40px; padding: 40px; max-width: 850px; margin: 0 auto; }
                .est-name-highlight { font-size: 2.8rem; font-weight: 900; background: linear-gradient(to right, #ffffff, #94a3b8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
                .glass-card { background: white; border-radius: 24px; padding: 20px; display: flex; align-items: center; gap: 15px; cursor: pointer; transition: 0.3s; }
                .glass-card.active { border: 2px solid #38bdf8; transform: scale(1.02); }
                .card-icon { width: 55px; height: 55px; border-radius: 16px; display: flex; justify-content: center; align-items: center; color: white; font-size: 22px; flex-shrink: 0; }
                .card-text h3 { color: #0f172a; font-size: 17px; font-weight: 800; margin: 0; }
                .card-text p { color: #64748b; font-size: 13px; margin: 0; }
                .chevron { margin-right: auto; color: #cbd5e1; transition: 0.3s; }
                .chevron.rotate { transform: rotate(180deg); }
                .store-icon-wrapper { width: 70px; height: 70px; background: #fbbf24; border-radius: 22px; display: flex; align-items: center; justify-content: center; font-size: 2rem; color: #0f172a; margin: 0 auto 15px; }
                .gov-badge { display: inline-flex; align-items: center; background: rgba(56, 189, 248, 0.1); color: #38bdf8; padding: 8px 20px; border-radius: 100px; font-size: 0.9rem; font-weight: 600; }
            `}</style>
        </div>
    );
}